import type { BankItem } from "../../engine/bank-types";

/**
 * 語境解讀 — why is this said here, at this moment, to this person?
 *
 * A sentence lifted out of its occasion loses most of its meaning. These
 * items restore the occasion: who is speaking, what has just happened, and
 * what the remark is doing in that situation.
 */
export const INTERPRETATION_ITEMS: BankItem[] = [
  {
    id: "in-3-1",
    type: "interpretation",
    refs: [41, 42],
    concepts: ["li", "zhengming"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "《八佾》開篇兩章的編排用意",
    stem: "《八佾》篇以季氏八佾舞於庭、三家者以《雍》徹兩章開篇。這個編排的用意是？",
    options: [
      { id: "a", text: "先舉兩樁僭禮，為全篇立下實例" },
      { id: "b", text: "說明魯國的樂舞水準很高", why: "兩章都在指責規格僭越，與水準無關。" },
      { id: "c", text: "介紹周代的樂舞制度", why: "制度只是背景，章旨在批評。" },
      { id: "d", text: "表明孔子與季氏的私人恩怨", why: "批評的依據是名分之禮，非私怨。" },
    ],
    answerId: "a",
    explanation: "《論語》各篇的開頭常有意味。《八佾》從兩件正在發生的僭禮說起，後面「人而不仁如禮何」「禮之本」的討論就不是空談。",
  },
  {
    id: "in-5-9",
    type: "interpretation",
    refs: [101],
    concepts: ["zhiren", "xue"],
    figures: ["zigong", "yanhui"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "孔子問話的用意",
    stem: "孔子問子貢「女與回也孰愈？」子貢答「賜也何敢望回？回也聞一以知十，賜也聞一以知二」。孔子接著說「弗如也；吾與女弗如也」。這一問一答的用意是？",
    options: [
      { id: "a", text: "考察他有無自知之明，並認可其誠實" },
      { id: "b", text: "當眾貶低子貢，抬高顏回", why: "孔子把自己也算進去，語氣是同行而非貶抑。" },
      { id: "c", text: "測試子貢是否會謙虛地說假話", why: "「聞一知二」是實在的自我評估，孔子認可的正是這份準確。" },
      { id: "d", text: "說明顏回的天資無人可及，不必再學", why: "章旨在自知，不在天資定論。" },
    ],
    answerId: "a",
    explanation: "「吾與女弗如也」是全書最溫厚的一句。老師把自己放進「不如」的行列，既保全了學生，也肯定了他敢說真話。",
  },
  {
    id: "in-6-28",
    type: "interpretation",
    refs: [148],
    concepts: ["tianming", "chushi"],
    figures: ["zilu"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "誓辭出現的場合",
    stem: "「子見南子，子路不說。夫子矢之曰：『予所否者，天厭之！天厭之！』」孔子為何要發誓？",
    options: [
      { id: "a", text: "無從辯解，只能以重誓自明無不正" },
      { id: "b", text: "他要證明南子是個好人", why: "誓辭針對的是自己的行止，不是替南子辯護。" },
      { id: "c", text: "他習慣以發誓加強語氣", why: "全書僅此一處發誓，正因情況特殊。" },
      { id: "d", text: "他在向天祈求原諒", why: "「天厭之」是自誓之辭，非祈禱。" },
    ],
    answerId: "a",
    explanation: "全書唯一一次孔子發誓，而且連說兩遍。這一章難得地保留了師徒之間的尷尬——子路的耿直讓老師無從辯解，只能賭咒。",
  },
  {
    id: "in-7-11",
    type: "interpretation",
    refs: [161],
    concepts: ["chushi", "junzi"],
    figures: ["yanhui", "zilu"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "一句話同時對兩個人說",
    stem: "孔子對顏淵說「用之則行，舍之則藏，惟我與爾有是夫」，子路立刻問「子行三軍，則誰與？」這個插話顯示？",
    options: [
      { id: "a", text: "他另起自己擅長的戰場來爭取認可" },
      { id: "b", text: "子路對軍事有專業興趣，順口請教", why: "上下文緊接著那句「惟我與爾」，插話動機明顯。" },
      { id: "c", text: "子路認為顏淵不適合帶兵，出言提醒", why: "他問的是「誰與」，指向的是自己。" },
      { id: "d", text: "孔子刻意冷落子路，引他發問", why: "章中無此設計；孔子隨即以「暴虎馮河」抑其勇。" },
    ],
    answerId: "a",
    explanation: "這是《論語》敘事最精妙的地方：不寫子路的心情，只記他插了一句話。而孔子的回答「暴虎馮河，死而無悔者，吾不與也」把他的心思也一併點破了。",
  },
  {
    id: "in-9-5",
    type: "interpretation",
    refs: [214],
    concepts: ["tianming", "chushi"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "危難中的自我定位",
    stem: "「子畏於匡」時，孔子說「文王既沒，文不在茲乎？天之將喪斯文也，後死者不得與於斯文也」。這番話的性質是？",
    options: [
      { id: "a", text: "以文化傳承的使命自我定位而安身" },
      { id: "b", text: "他確信自己不會死，因為有天保佑", why: "他的推理是條件式的：若天要喪斯文，我也就無從參與——並非保命的斷言。" },
      { id: "c", text: "他在向匡人說明自己的身分以求脫困", why: "這是對隨行者說的話，非交涉之辭。" },
      { id: "d", text: "他認為文王的地位高於自己", why: "「文不在茲乎」正是自任其傳。" },
    ],
    answerId: "a",
    explanation: "危險當前，他想的不是活不活得下來，而是「這套文化在不在我身上」。這種在極端處境下的自我定位，是 7.23「天生德於予」的同一姿態。",
  },
  {
    id: "in-11-9",
    type: "interpretation",
    refs: [279, 280],
    concepts: ["tianming", "ren"],
    figures: ["yanhui"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「非夫人之為慟而誰為」的分量",
    stem: "顏淵死，孔子哭之慟。從者說「子慟矣」，孔子答「有慟乎？非夫人之為慟而誰為！」這句話的意思是？",
    options: [
      { id: "a", text: "知道自己失態，仍認為此處正該如此" },
      { id: "b", text: "他否認自己過度悲傷", why: "「有慟乎」是反問而非否認，下句立即承認並辯護。" },
      { id: "c", text: "他責備從者多管閒事", why: "他回答的是該不該慟，不是責備。" },
      { id: "d", text: "他認為哭泣不合禮，需要收斂", why: "他隨即為自己的慟辯護，且 11.11 才是他守禮的地方。" },
    ],
    answerId: "a",
    explanation: "從者提醒他「過了」，他承認過了，但堅持這一次應該過。禮的分寸在他這裡不是機械執行——什麼時候該破例，本身也是判斷。",
  },
  {
    id: "in-13-3",
    type: "interpretation",
    refs: [349],
    concepts: ["zhengming"],
    figures: ["zilu"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "子路的質疑使論證展開",
    stem: "子路說「有是哉，子之迂也！奚其正？」孔子隨即斥「野哉由也」並展開整條推論。這段對話的作用是？",
    options: [
      { id: "a", text: "學生的質疑逼出了最完整的一次論證" },
      { id: "b", text: "顯示子路不尊重老師，應受批評", why: "「野哉」雖是責語，但孔子隨即認真作答，可見他重視這個質疑。" },
      { id: "c", text: "顯示孔子脾氣不好", why: "責語之後是全書最有條理的推論之一。" },
      { id: "d", text: "說明正名之說確實迂闊", why: "推論的結論恰恰證明它不迂。" },
    ],
    answerId: "a",
    explanation: "《論語》裡最好的段落常由一句頂撞開始。子路一句「子之迂也」，換來「名不正則言不順」一整條因果鏈——這是質疑的價值。",
  },
  {
    id: "in-15-2",
    type: "interpretation",
    refs: [422],
    concepts: ["junzi", "chushi"],
    figures: ["zilu"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "「君子固窮」的處境",
    stem: "在陳絕糧，從者病，子路慍見曰「君子亦有窮乎？」孔子答「君子固窮，小人窮斯濫矣」。這句答話的重點是？",
    options: [
      { id: "a", text: "君子也會窮，別在窮時守不守得住" },
      { id: "b", text: "君子本來就應該貧窮，安於清苦", why: "「固窮」是「本來就會有窮的時候」或「固守於窮」，不是以貧為目標。" },
      { id: "c", text: "他責備子路不該抱怨", why: "答語是就處境立論，不是責備。" },
      { id: "d", text: "他保證困境很快會過去", why: "全章沒有這樣的安慰。" },
    ],
    answerId: "a",
    explanation: "子路的問題其實是：講道德有什麼用，還不是餓肚子。孔子沒有辯解，只指出差別在窮的時候會不會亂來——這比任何許諾都更誠實。",
  },
  {
    id: "in-17-1",
    type: "interpretation",
    refs: [477],
    concepts: ["li", "chushi"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "禮的形式如何用於周旋",
    stem: "陽貨送豚給孔子，孔子「時其亡也，而往拜之」，卻在路上遇見了他。孔子這一連串舉動說明？",
    options: [
      { id: "a", text: "不願依附又不肯失禮，故以禮周旋" },
      { id: "b", text: "他其實很想見陽貨，只是不好意思", why: "特意挑對方不在家時回拜，正是為了不見。" },
      { id: "c", text: "他忘記了陽貨在家的時間", why: "「時其亡也」是刻意挑選，不是疏忽。" },
      { id: "d", text: "他完全不理會陽貨，拒絕往來", why: "他確實去回拜了，禮數並未廢。" },
    ],
    answerId: "a",
    explanation: "禮在這裡成了一種精密的社交工具：該還的禮還了，不想見的人也沒見成。可惜路上遇見——《論語》連這種尷尬也照實記下。",
  },
  {
    id: "in-18-6",
    type: "interpretation",
    refs: [508],
    concepts: ["chushi", "ren"],
    figures: ["zilu"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "孔子對隱者的正面回應",
    stem: "長沮桀溺勸子路「與其從辟人之士，豈若從辟世之士哉」。孔子聽後憮然曰「鳥獸不可與同群……天下有道，丘不與易也」。他的立場是？",
    options: [
      { id: "a", text: "正因天下無道，才需要有人去改變" },
      { id: "b", text: "他鄙視隱者，認為他們懦弱", why: "「憮然」是悵然若失，語氣中有理解，並非鄙視。" },
      { id: "c", text: "他承認隱者說得對，只是自己放不下", why: "「丘不與易也」是明確的立場陳述，非無奈。" },
      { id: "d", text: "他認為隱居才是更高的境界", why: "與「鳥獸不可與同群」相反。" },
    ],
    answerId: "a",
    explanation: "全書最有分量的一次交鋒。隱者的邏輯無懈可擊，孔子沒有反駁它，只是說出自己的選擇——「憮然」二字，把那份沉重也留下了。",
  },
  {
    id: "in-11-26",
    type: "interpretation",
    refs: [296],
    concepts: ["chushi", "li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "以「毋吾以也」開場的意義",
    stem: "四子侍坐，孔子先說「以吾一日長乎爾，毋吾以也」。這句開場白的作用是？",
    options: [
      { id: "a", text: "先解除輩分壓力，讓學生敢說真話" },
      { id: "b", text: "強調自己年長，學生應當恭敬", why: "「毋吾以也」正是要他們不要因此拘束。" },
      { id: "c", text: "說明自己時間不多，要學生長話短說", why: "「一日長乎爾」指年長，不指時間緊迫。" },
      { id: "d", text: "為接下來的批評先作鋪墊", why: "他隨後並未系統批評，只評子路「其言不讓」。" },
    ],
    answerId: "a",
    explanation: "整章的從容氣氛從這句話開始。也正因如此，曾晳才敢在別人都談政事時，說出暮春浴沂的那一段——場面是老師先造出來的。",
  },
  {
    id: "in-2-21",
    type: "interpretation",
    refs: [37],
    concepts: ["zheng", "xiao"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "回應「子奚不為政」的方式",
    stem: "有人問孔子「子奚不為政？」他引《書》「孝乎惟孝，友于兄弟，施於有政」作答。這個回答的策略是？",
    options: [
      { id: "a", text: "擴大為政的定義：行孝悌即是為政" },
      { id: "b", text: "承認自己沒有從政的能力", why: "他的回答是重新界定為政，不是自認不能。" },
      { id: "c", text: "批評當時的政治太黑暗", why: "章中未涉時政批評。" },
      { id: "d", text: "表示自己志在治學，無意仕途", why: "他一生求仕，且此答並未拒絕從政。" },
    ],
    answerId: "a",
    explanation: "被問到痛處，他不辯解也不迴避，而是換掉問題的前提——如果為政的實質是使風俗歸厚，那麼他一直在做。",
  },
  {
    id: "in-14-21",
    type: "interpretation",
    refs: [397],
    concepts: ["li", "zheng", "chushi"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "沐浴而朝的儀式意義",
    stem: "陳成子弒簡公，孔子「沐浴而朝」，請哀公討伐。他為何要先沐浴？",
    options: [
      { id: "a", text: "以最鄭重的禮，表明事關名分綱紀" },
      { id: "b", text: "當時面見國君都必須沐浴", why: "若是常例，記載就不必特別點出。" },
      { id: "c", text: "他當時已經退休，需要正式的身分", why: "他確以「從大夫之後」自陳，但沐浴的意義在鄭重其事。" },
      { id: "d", text: "他要參加祭祀活動", why: "他是為請討而朝，非為祭祀。" },
    ],
    answerId: "a",
    explanation: "一個已不當政的老人，為一件別國的事，鄭重沐浴後上朝請討——請求被拒後他說「以吾從大夫之後，不敢不告也」。禮在這裡是責任的形式。",
  },
  {
    id: "in-6-4",
    type: "interpretation",
    refs: [124, 125],
    concepts: ["yili", "ren"],
    figures: ["ranyou"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "兩章並列的對照用意",
    stem: "6.4（子華使齊，冉子為其母請粟，孔子只給少量，冉有卻多給）與 6.5（原思辭祿，孔子要他收下並分給鄰里）並列。編排的用意是？",
    options: [
      { id: "a", text: "以對照立周濟之則：周急而不繼富" },
      { id: "b", text: "說明孔子對弟子偏心，賞罰不均", why: "兩章的差別出自受助者的處境，不是親疏。" },
      { id: "c", text: "說明孔子理財嚴格，不肯多花錢", why: "6.5 他反而要原思不要推辭，可見不是吝惜。" },
      { id: "d", text: "記錄孔門的俸祿制度", why: "制度只是背景，章旨在給與不給的判準。" },
    ],
    answerId: "a",
    explanation: "子華出使有車馬輕裘，不急；原思為宰而辭祿，是該得的。同一個孔子，一邊減一邊勸收——判準是需要，不是人情。",
  },
  {
    id: "in-19-25",
    type: "interpretation",
    refs: [538, 537],
    concepts: ["zhiren", "li"],
    figures: ["zigong"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "《子張》篇末四章的共同主題",
    stem: "《子張》篇末連續數章都記子貢回應他人對孔子的貶抑或抬高子貢自己。這組編排說明？",
    options: [
      { id: "a", text: "弟子為師門定位，確立孔子的地位" },
      { id: "b", text: "子貢與同門爭奪學派領導權", why: "他每次都在推辭對自己的抬高，正好相反。" },
      { id: "c", text: "當時人普遍認為子貢勝過孔子", why: "有此議論，但這幾章記的正是子貢的反駁。" },
      { id: "d", text: "這幾章是後人偽託", why: "無版本依據，且與子貢一貫的言語風格相合。" },
    ],
    answerId: "a",
    explanation: "全書將盡處，記的不是孔子而是弟子護師。「仲尼，日月也，無得而踰焉」——這是《論語》編者留下的最後定調。",
  },
  {
    id: "in-9-12",
    type: "interpretation",
    refs: [221],
    concepts: ["li", "xin"],
    figures: ["zilu"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「無臣而為有臣」的責備",
    stem: "孔子病重，子路讓門人充當家臣以備喪事。孔子病稍好後責備「久矣哉，由之行詐也！無臣而為有臣，吾誰欺？欺天乎？」。他生氣的是？",
    options: [
      { id: "a", text: "以不合身分的排場治喪，等於作假" },
      { id: "b", text: "子路擅自作主，沒有請示他", why: "責備的落點是「行詐」與名實不副，不是程序。" },
      { id: "c", text: "子路為此花費了太多錢財", why: "章中並未提到費用多寡，責備的也不是開銷。" },
      { id: "d", text: "子路預備喪事等於咒他死", why: "備喪出於盡心，孔子並未往這個方向理解。" },
    ],
    answerId: "a",
    explanation: "子路完全出於好意，想讓老師走得體面。孔子看到的卻是名實不副的假——「吾誰欺？欺天乎」，這是他責備學生時最重的一次。",
  },
  {
    id: "in-5-26",
    type: "interpretation",
    refs: [118],
    concepts: ["ren", "junzi"],
    figures: ["zilu", "yanhui"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "三種志向的格局差異",
    stem: "顏淵季路侍，各言爾志：子路「願車馬衣裘與朋友共，敝之而無憾」，顏淵「願無伐善，無施勞」，孔子「老者安之，朋友信之，少者懷之」。三者的差別是？",
    options: [
      { id: "a", text: "由與友共物，到不自誇，到各得其所" },
      { id: "b", text: "三人志向相同，只是說法不同", why: "一個對朋友，一個對自己，一個對天下各色人，範圍層層擴大。" },
      { id: "c", text: "子路最高，因為最慷慨", why: "慷慨是一面，但格局仍限於朋友之間。" },
      { id: "d", text: "孔子的志向最容易做到", why: "使老者安、朋友信、少者懷，是三者中最難的。" },
    ],
    answerId: "a",
    explanation: "三句話排在一起，格局一目了然：從物到己到人。孔子那句沒有一個字說自己，全是別人該得到什麼——這是他一生的目標陳述。",
  },
];
